import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  CreditCard, 
  TrendingUp, 
  Gift,
  ArrowLeft,
  Briefcase,
  Shield,
  Loader2,
  Plus,
  Trash2,
  Copy,
  Check,
  DollarSign,
  UserPlus,
  Activity,
  HelpCircle,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  Mail,
  Chrome,
  Settings,
  FileText,
  Download,
  FolderOpen,
  RefreshCw,
  RotateCcw,
  PenSquare,
  Eye
} from "lucide-react";
import BlogManagement from "@/components/admin/BlogManagement";
import UserSessions from "@/components/admin/UserSessions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
  withCredentials: true
});

// Add Authorization header from localStorage token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('hireun_session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses globally (session expiration)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('hireun_session_token');
      localStorage.removeItem('hireun_user');
      window.location.href = '/login?expired=true';
    }
    return Promise.reject(error);
  }
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [ticketFilter, setTicketFilter] = useState("all");
  const [resettingPreps, setResettingPreps] = useState(null); // user_id being reset
  
  // New promo form state
  const [newPromo, setNewPromo] = useState({
    code: "",
    type: "extra_preps",
    value: 10,
    max_uses: null,
    expires_at: ""
  });
  const [isCreatingPromo, setIsCreatingPromo] = useState(false);

  // Reset user preps function
  const resetUserPreps = async (userId, userEmail) => {
    if (!confirm(`Reset prep count to 0 for ${userEmail}? This will allow them to generate more preps.`)) {
      return;
    }
    
    setResettingPreps(userId);
    try {
      const response = await apiClient.post(`/admin/users/${userId}/reset-preps`, {
        user_id: userId,
        new_preps_used: 0
      });
      toast.success(`Reset preps for ${userEmail}: ${response.data.previous_preps_used} → 0`);
      // Refresh user list
      const usersRes = await apiClient.get("/admin/users");
      setUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error("Failed to reset preps");
      console.error(error);
    } finally {
      setResettingPreps(null);
    }
  };

  // Bulk reset all free users
  const bulkResetPreps = async () => {
    if (!confirm("Reset prep count to 0 for ALL free users? This cannot be undone.")) {
      return;
    }
    
    setResettingPreps("bulk");
    try {
      const response = await apiClient.post("/admin/users/bulk-reset-preps");
      toast.success(`Reset preps for ${response.data.users_affected} free users`);
      // Refresh user list
      const usersRes = await apiClient.get("/admin/users");
      setUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error("Failed to bulk reset preps");
      console.error(error);
    } finally {
      setResettingPreps(null);
    }
  };

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setIsLoading(true);
    try {
      // Debug: Check if token exists
      const token = localStorage.getItem('hireun_session_token');
      console.log('Admin load - Token exists:', !!token);
      
      const [statsRes, usersRes, promoRes, ticketsRes, docsRes] = await Promise.all([
        apiClient.get("/admin/stats"),
        apiClient.get("/admin/users"),
        apiClient.get("/admin/promo-codes"),
        apiClient.get("/admin/support/tickets"),
        apiClient.get("/admin/documents").catch(() => ({ data: { documents: [] } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setPromoCodes(promoRes.data.promo_codes || []);
      setSupportTickets(ticketsRes.data.tickets || []);
      setDocuments(docsRes.data.documents || []);
    } catch (error) {
      console.error('Admin data load error:', error.response?.status, error.response?.data || error.message);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/app");
      } else if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/login");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createPromoCode = async () => {
    if (!newPromo.code.trim()) {
      toast.error("Please enter a promo code");
      return;
    }
    
    setIsCreatingPromo(true);
    try {
      const payload = {
        ...newPromo,
        code: newPromo.code.toUpperCase(),
        max_uses: newPromo.max_uses || null,
        expires_at: newPromo.expires_at || null
      };
      
      await apiClient.post("/admin/promo-codes", payload);
      toast.success("Promo code created!");
      setShowCreatePromo(false);
      setNewPromo({ code: "", type: "extra_preps", value: 10, max_uses: null, expires_at: "" });
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promo code");
    } finally {
      setIsCreatingPromo(false);
    }
  };

  const deletePromoCode = async (code) => {
    try {
      await apiClient.delete(`/admin/promo-codes/${code}`);
      toast.success("Promo code deleted");
      setPromoCodes(prev => prev.filter(p => p.code !== code));
    } catch (error) {
      toast.error("Failed to delete promo code");
    }
  };

  const copyCode = async (code) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Copied!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      await apiClient.patch(`/admin/support/tickets/${ticketId}`, { status: newStatus });
      toast.success("Ticket updated");
      setSupportTickets(prev => prev.map(t => 
        t.ticket_id === ticketId ? { ...t, status: newStatus } : t
      ));
    } catch (error) {
      toast.error("Failed to update ticket");
    }
  };

  const downloadDocument = async (documentId) => {
    try {
      const response = await apiClient.get(`/admin/documents/${documentId}`);
      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank');
        toast.success("Download started");
      } else {
        toast.error("Download URL not available");
      }
    } catch (error) {
      toast.error("Failed to get download link");
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document? This cannot be undone.")) {
      return;
    }
    try {
      await apiClient.delete(`/admin/documents/${documentId}`);
      toast.success("Document deleted");
      setDocuments(prev => prev.filter(d => d.document_id !== documentId));
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const filteredTickets = supportTickets.filter(t => 
    ticketFilter === "all" || t.status === ticketFilter
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/app")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to App
              </Button>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-500" />
                <span className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Admin Dashboard
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate("/admin/settings")} data-testid="admin-settings-btn">
                <Settings className="w-4 h-4 mr-2" />
                Security
              </Button>
              <Badge className="bg-red-500 text-white">
                <Shield className="w-3 h-3 mr-1" />
                Founder Mode
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-3xl font-bold">{stats?.users?.total || 0}</p>
                  <p className="text-xs text-green-600">+{stats?.users?.new_24h || 0} today</p>
                </div>
                <Users className="w-10 h-10 text-primary/20" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Premium Users</p>
                  <p className="text-3xl font-bold">{stats?.users?.premium || 0}</p>
                  <p className="text-xs text-muted-foreground">{stats?.users?.trial || 0} on trial</p>
                </div>
                <CreditCard className="w-10 h-10 text-amber-500/20" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                  <p className="text-3xl font-bold">€{stats?.revenue?.mrr || 0}</p>
                  <p className="text-xs text-muted-foreground">{stats?.revenue?.active_subscriptions || 0} active subs</p>
                </div>
                <DollarSign className="w-10 h-10 text-green-500/20" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Preps</p>
                  <p className="text-3xl font-bold">{stats?.preps?.total_generated || 0}</p>
                  <p className="text-xs text-muted-foreground">{stats?.preps?.avg_per_user || 0} avg/user</p>
                </div>
                <Activity className="w-10 h-10 text-purple-500/20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  <p className="text-3xl font-bold">{stats?.users?.conversion_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Free → Premium</p>
                </div>
                <TrendingUp className="w-10 h-10 text-blue-500/20" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Google Signups</p>
                  <p className="text-3xl font-bold" data-testid="google-users-count">{stats?.users?.by_auth_method?.google || 0}</p>
                  <p className="text-xs text-muted-foreground">OAuth users</p>
                </div>
                <Chrome className="w-10 h-10 text-blue-500/20" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Email Signups</p>
                  <p className="text-3xl font-bold" data-testid="email-users-count">{stats?.users?.by_auth_method?.email || 0}</p>
                  <p className="text-xs text-muted-foreground">Password users</p>
                </div>
                <Mail className="w-10 h-10 text-gray-500/20" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Open Tickets</p>
                  <p className="text-3xl font-bold">{stats?.support?.open_tickets || 0}</p>
                  <p className="text-xs text-muted-foreground">Needs attention</p>
                </div>
                <HelpCircle className="w-10 h-10 text-orange-500/20" />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Quick Stats Row 3 */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">AI Chat Usage</p>
                  <p className="text-3xl font-bold">{stats?.preps?.ai_chat_messages || 0}</p>
                  <p className="text-xs text-muted-foreground">Total messages</p>
                </div>
                <MessageSquare className="w-10 h-10 text-indigo-500/20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Connection Analytics */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Connection Analytics
            </CardTitle>
            <CardDescription>Track user logins per day, month, and year</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Today */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="font-medium text-blue-800">Today</span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-600">Total Logins</span>
                    <span className="text-lg font-bold text-blue-800">{stats?.connections?.today?.total_logins || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-600">Unique Users</span>
                    <span className="text-lg font-bold text-blue-800">{stats?.connections?.today?.unique_users || 0}</span>
                  </div>
                </div>
              </div>
              
              {/* This Month */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="font-medium text-green-800">This Month</span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-green-600">Total Logins</span>
                    <span className="text-lg font-bold text-green-800">{stats?.connections?.this_month?.total_logins || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-green-600">Unique Users</span>
                    <span className="text-lg font-bold text-green-800">{stats?.connections?.this_month?.unique_users || 0}</span>
                  </div>
                </div>
              </div>
              
              {/* This Year */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="font-medium text-purple-800">This Year</span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-purple-600">Total Logins</span>
                    <span className="text-lg font-bold text-purple-800">{stats?.connections?.this_year?.total_logins || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-purple-600">Unique Users</span>
                    <span className="text-lg font-bold text-purple-800">{stats?.connections?.this_year?.unique_users || 0}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Daily Logins Chart (Last 30 Days) */}
            {stats?.connections?.by_day?.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Daily Logins (Last 30 Days)</h4>
                <div className="flex items-end gap-1 h-24 bg-muted/30 rounded p-2">
                  {stats.connections.by_day.map((day, idx) => {
                    const maxCount = Math.max(...stats.connections.by_day.map(d => d.count), 1);
                    const height = (day.count / maxCount) * 100;
                    return (
                      <div
                        key={day.date}
                        className="flex-1 bg-primary/70 hover:bg-primary rounded-t transition-colors cursor-pointer group relative"
                        style={{ height: `${Math.max(height, 5)}%` }}
                        title={`${day.date}: ${day.count} logins`}
                      >
                        <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                          {day.date}: {day.count}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Monthly Logins (Last 12 Months) */}
            {stats?.connections?.by_month?.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Monthly Logins (Last 12 Months)</h4>
                <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                  {stats.connections.by_month.map((month) => (
                    <div key={month.month} className="text-center p-2 bg-muted/30 rounded">
                      <div className="text-lg font-bold text-primary">{month.count}</div>
                      <div className="text-xs text-muted-foreground">{month.month.slice(5)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="sessions" data-testid="sessions-tab">
              <Eye className="w-4 h-4 mr-2" />
              Support Access
            </TabsTrigger>
            <TabsTrigger value="blog" data-testid="blog-tab">
              <PenSquare className="w-4 h-4 mr-2" />
              Blog
            </TabsTrigger>
            <TabsTrigger value="promo">
              <Gift className="w-4 h-4 mr-2" />
              Promo Codes ({promoCodes.length})
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="documents-tab">
              <FolderOpen className="w-4 h-4 mr-2" />
              Documents ({documents.length})
            </TabsTrigger>
            <TabsTrigger value="support">
              <HelpCircle className="w-4 h-4 mr-2" />
              Support ({supportTickets.filter(t => t.status === "open").length} open)
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>Manage and view user accounts</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={bulkResetPreps}
                  disabled={resettingPreps === "bulk"}
                  data-testid="bulk-reset-preps"
                >
                  {resettingPreps === "bulk" ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Reset All Free Users
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">User</th>
                        <th className="text-left py-3 px-2">Auth Method</th>
                        <th className="text-left py-3 px-2">Role</th>
                        <th className="text-left py-3 px-2">Preps Used</th>
                        <th className="text-left py-3 px-2">Reset</th>
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-left py-3 px-2">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-b hover:bg-secondary/20" data-testid={`user-row-${u.user_id}`}>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              {u.picture && (
                                <img src={u.picture} alt="" className="w-6 h-6 rounded-full" />
                              )}
                              <div>
                                <p className="font-medium">{u.name || "Unknown"}</p>
                                <p className="text-xs text-muted-foreground">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <Badge 
                              variant="outline" 
                              className={u.auth_method === "google" ? "border-blue-500 text-blue-600" : "border-gray-400 text-gray-600"}
                              data-testid={`auth-method-${u.user_id}`}
                            >
                              {u.auth_method === "google" ? (
                                <><Chrome className="w-3 h-3 mr-1" /> Google</>
                              ) : (
                                <><Mail className="w-3 h-3 mr-1" /> Email</>
                              )}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            <Badge variant={u.role === "admin" ? "destructive" : u.role === "premium" ? "default" : "secondary"}>
                              {u.role}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">{u.preps_used || 0}</td>
                          <td className="py-3 px-2">
                            {u.role !== "admin" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => resetUserPreps(u.user_id, u.email)}
                                disabled={resettingPreps === u.user_id}
                                title="Reset prep count to 0"
                                data-testid={`reset-preps-${u.user_id}`}
                              >
                                {resettingPreps === u.user_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {u.subscription_status && (
                              <Badge variant="outline">{u.subscription_status}</Badge>
                            )}
                          </td>
                          <td className="py-3 px-2 text-muted-foreground">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Sessions Tab (Support Access) */}
          <TabsContent value="sessions">
            <UserSessions 
              apiClient={apiClient}
              onImpersonate={(impersonationData) => {
                // Store impersonation data and redirect to app
                localStorage.setItem('hireun_impersonation', JSON.stringify(impersonationData));
                window.location.href = `/app?impersonation_token=${impersonationData.impersonation_token}`;
              }}
            />
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <BlogManagement />
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promo">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Promo Codes</CardTitle>
                  <CardDescription>Create and manage promotional codes</CardDescription>
                </div>
                <Button onClick={() => setShowCreatePromo(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Code
                </Button>
              </CardHeader>
              <CardContent>
                {promoCodes.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Gift className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No promo codes yet</p>
                    <p className="text-sm">Create your first code to drive growth</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Code</th>
                          <th className="text-left py-3 px-2">Type</th>
                          <th className="text-left py-3 px-2">Value</th>
                          <th className="text-left py-3 px-2">Uses</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-left py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {promoCodes.map((promo) => (
                          <tr key={promo.code} className="border-b hover:bg-secondary/20">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <code className="bg-secondary px-2 py-1 rounded font-mono">
                                  {promo.code}
                                </code>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => copyCode(promo.code)}
                                >
                                  {copiedCode === promo.code ? (
                                    <Check className="w-3 h-3 text-green-500" />
                                  ) : (
                                    <Copy className="w-3 h-3" />
                                  )}
                                </Button>
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <Badge variant="outline">
                                {promo.type === "extra_preps" ? "Extra Preps" : "Premium Trial"}
                              </Badge>
                            </td>
                            <td className="py-3 px-2">
                              {promo.type === "extra_preps" 
                                ? `+${promo.value} preps` 
                                : `${promo.value} days`}
                            </td>
                            <td className="py-3 px-2">
                              {promo.uses}{promo.max_uses ? `/${promo.max_uses}` : ""}
                            </td>
                            <td className="py-3 px-2">
                              <Badge variant={promo.is_active ? "default" : "secondary"}>
                                {promo.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td className="py-3 px-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => deletePromoCode(promo.code)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab (GED) */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderOpen className="w-5 h-5" />
                  Document Storage (GED)
                </CardTitle>
                <CardDescription>View and manage uploaded CVs and documents. Files are stored securely in S3.</CardDescription>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No documents uploaded yet</p>
                    <p className="text-sm">CVs will appear here when users upload them</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">File Name</th>
                          <th className="text-left py-3 px-2">User</th>
                          <th className="text-left py-3 px-2">Type</th>
                          <th className="text-left py-3 px-2">Size</th>
                          <th className="text-left py-3 px-2">Confidence</th>
                          <th className="text-left py-3 px-2">Uploaded</th>
                          <th className="text-left py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc) => (
                          <tr key={doc.document_id} className="border-b hover:bg-secondary/20" data-testid={`doc-row-${doc.document_id}`}>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium truncate max-w-[200px]" title={doc.original_filename}>
                                  {doc.original_filename}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div>
                                <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                                  {doc.user_email || doc.user_id}
                                </p>
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <Badge variant="outline">{doc.document_type || "cv"}</Badge>
                            </td>
                            <td className="py-3 px-2 text-muted-foreground">
                              {doc.size_bytes ? `${(doc.size_bytes / 1024).toFixed(1)} KB` : "N/A"}
                            </td>
                            <td className="py-3 px-2">
                              <Badge variant={doc.cv_confidence === "high" ? "default" : doc.cv_confidence === "medium" ? "secondary" : "outline"}>
                                {doc.cv_confidence || "unknown"}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-muted-foreground">
                              {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : "N/A"}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => downloadDocument(doc.document_id)}
                                  title="Download"
                                  data-testid={`download-doc-${doc.document_id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => deleteDocument(doc.document_id)}
                                  title="Delete"
                                  data-testid={`delete-doc-${doc.document_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tickets Tab */}
          <TabsContent value="support">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Support Tickets</CardTitle>
                  <CardDescription>Manage user support requests</CardDescription>
                </div>
                <Select value={ticketFilter} onValueChange={setTicketFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tickets</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                {filteredTickets.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No support tickets</p>
                    <p className="text-sm">Tickets from users will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredTickets.map((ticket) => (
                      <div key={ticket.ticket_id} className="border rounded-lg p-4 hover:bg-secondary/10">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={
                                ticket.category === "bug" ? "destructive" : 
                                ticket.category === "feature" ? "default" : "secondary"
                              }>
                                {ticket.category}
                              </Badge>
                              <Badge variant={
                                ticket.status === "open" ? "outline" :
                                ticket.status === "in_progress" ? "secondary" : "default"
                              }>
                                {ticket.status === "open" && <AlertCircle className="w-3 h-3 mr-1" />}
                                {ticket.status === "resolved" && <CheckCircle className="w-3 h-3 mr-1" />}
                                {ticket.status.replace("_", " ")}
                              </Badge>
                              <Badge variant="outline">{ticket.user_role}</Badge>
                            </div>
                            <h4 className="font-medium">{ticket.subject}</h4>
                            <p className="text-sm text-muted-foreground mt-1">{ticket.message}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>{ticket.user_email}</span>
                              <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {ticket.status !== "resolved" && (
                              <>
                                {ticket.status === "open" && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => updateTicketStatus(ticket.ticket_id, "in_progress")}
                                  >
                                    Start
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="default"
                                  onClick={() => updateTicketStatus(ticket.ticket_id, "resolved")}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Resolve
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Promo Dialog */}
      <Dialog open={showCreatePromo} onOpenChange={setShowCreatePromo}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
            <DialogDescription>
              Create a new promotional code for users
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Code</label>
              <Input 
                placeholder="e.g., WELCOME50"
                value={newPromo.code}
                onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Type</label>
              <Select 
                value={newPromo.type}
                onValueChange={(v) => setNewPromo({ ...newPromo, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="extra_preps">Extra Preps (+N preps)</SelectItem>
                  <SelectItem value="premium_trial">Premium Trial (N days)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">
                {newPromo.type === "extra_preps" ? "Number of Preps" : "Trial Days"}
              </label>
              <Input 
                type="number"
                min="1"
                value={newPromo.value}
                onChange={(e) => setNewPromo({ ...newPromo, value: parseInt(e.target.value) || 0 })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Max Uses (optional)</label>
              <Input 
                type="number"
                min="1"
                placeholder="Unlimited"
                value={newPromo.max_uses || ""}
                onChange={(e) => setNewPromo({ ...newPromo, max_uses: parseInt(e.target.value) || null })}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Expires At (optional)</label>
              <Input 
                type="datetime-local"
                value={newPromo.expires_at}
                onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePromo(false)}>
              Cancel
            </Button>
            <Button onClick={createPromoCode} disabled={isCreatingPromo}>
              {isCreatingPromo && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
